import React from "react";
import { Plus, Trash2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { EncryptedConversationMeta } from "@/hooks/useMindfulVault";

interface Props {
  conversations: EncryptedConversationMeta[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
  onLock: () => void;
}

const ConversationHistoryPanel: React.FC<Props> = ({
  conversations,
  activeId,
  onSelect,
  onDelete,
  onNew,
  onLock,
}) => (
  <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-background/40 backdrop-blur-2xl rounded-3xl p-3 m-3 mr-0">
    <Button onClick={onNew} variant="ghost" className="justify-start gap-2 rounded-2xl mb-2">
      <Plus className="h-4 w-4" /> New conversation
    </Button>

    <div className="flex-1 min-h-0 overflow-y-auto space-y-1">
      {conversations.length === 0 ? (
        <p className="text-xs text-muted-foreground italic px-2 py-4">
          Your encrypted conversations will appear here.
        </p>
      ) : (
        conversations.map((c) => (
          <div
            key={c.id}
            className={cn(
              "group flex items-center gap-1 rounded-2xl px-3 py-2 transition-colors",
              activeId === c.id ? "bg-primary/15" : "hover:bg-muted/40"
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(c.id)}
              className="flex-1 text-left min-w-0"
            >
              <span className="block truncate text-sm text-foreground">{c.title}</span>
              <span className="block text-[11px] text-muted-foreground">
                {new Date(c.updatedAt).toLocaleDateString()}
              </span>
            </button>
            <button
              type="button"
              onClick={() => onDelete(c.id)}
              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
              title="Delete conversation"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))
      )}
    </div>

    <Button onClick={onLock} variant="ghost" size="sm" className="justify-start gap-2 rounded-2xl mt-2 text-muted-foreground">
      <Lock className="h-3.5 w-3.5" /> Lock history
    </Button>
  </aside>
);

export default ConversationHistoryPanel;
